import { theme } from 'styles';
import { CSSObject } from '@emotion/react';

type VariantType = 'contained' | 'border' | 'text';
type ColorType = keyof typeof theme.color;

const { color: c, radius } = theme;

const NEUTRAL_COLORS: ColorType[] = [
  'primary',
  'black',
  'white',
  'grey',
  'background',
  'border',
  'text',
  'textSub',
  'grey50',
  'grey75',
  'grey100',
  'grey200',
  'grey300',
  'grey400',
  'grey500',
  'grey600',
  'grey700',
  'grey800',
  'grey900',
];

const isNeutral = (color: ColorType) => NEUTRAL_COLORS.includes(color);

const LIGHT_BACKGROUNDS: ColorType[] = [
  'white',
  'background',
  'surface',
  'grey50',
  'grey75',
  'grey100',
  'grey200',
  'successBg',
  'errorBg',
  'infoBg',
  'lightBlue3',
  'disabled',
];

const contained = (color: ColorType): CSSObject => {
  const isLight = LIGHT_BACKGROUNDS.includes(color);
  const hover =
    color === 'primary' || color === 'black' || color === 'grey900'
      ? c.primaryHover
      : undefined;

  return {
    backgroundColor: c[color],
    color: isLight ? c.text : c.white,
    ...(color === 'white' && { boxShadow: `inset 0 0 0 1px ${c.border}` }),
    ...(color === 'background' && { color: c.grey700 }),
    '&:hover:not(:disabled)': hover
      ? { backgroundColor: hover }
      : { filter: 'brightness(0.95)' },
  };
};

const border = (color: ColorType): CSSObject => ({
  backgroundColor: c.white,
  color: color === 'grey' ? c.grey700 : c[color],
  boxShadow: `inset 0 0 0 1px ${isNeutral(color) ? c.grey200 : c[color]}`,
  '&:hover:not(:disabled)': {
    backgroundColor: c.grey50,
  },
});

const text = (color: ColorType): CSSObject => ({
  color: c[color],
  '&:hover:not(:disabled)': {
    backgroundColor: c.grey75,
  },
});

const button = (variant: VariantType, color: ColorType): CSSObject => {
  const common: CSSObject = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '8px 14px',
    fontSize: 14,
    lineHeight: 1.3,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    borderRadius: 10,
    transition:
      'background-color 0.15s ease, filter 0.15s ease, transform 0.1s ease',

    '&:active:not(:disabled)': {
      transform: 'scale(0.98)',
    },

    '&:disabled': {
      cursor: 'not-allowed',
      color: `${c.grey300} !important`,
      backgroundColor: `${c.grey75} !important`,
      boxShadow: 'none',
    },
  };

  if (variant === 'contained') {
    return { ...common, ...contained(color) };
  }

  if (variant === 'border') {
    return { ...common, ...border(color) };
  }

  if (variant === 'text') {
    return { ...common, ...text(color) };
  }

  return common;
};

const iconButton: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  marginRight: 4,
  borderRadius: 10,
  background: c.white,
  boxShadow: `inset 0 0 0 1px ${c.grey100}`,
  transition: 'background-color 0.15s ease',
  ':hover': {
    backgroundColor: c.grey50,
  },
};

const listBtnStyle = (color: string): CSSObject => ({
  color,
  background: c.grey75,
  borderRadius: radius.pill,
  height: 24,
  padding: '0 10px',
  fontSize: 12,
  fontWeight: 600,
});

export type { VariantType, ColorType };
export { button, listBtnStyle, iconButton };
